// Drawola Studio — deep-link detection.
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.
//
// The Studio is the default entry point, EXCEPT when the URL already points
// the app at a specific scene (collab room, shared JSON link, external scene
// URL, backend `?id=`) or at a library import. In those cases the app must
// boot straight into the editor exactly as it did before the Studio existed.
//
// Detection reuses the app's own helpers wherever they exist:
//   - `#room=…`       -> `isCollaborationLink` (excalidraw-app/data/index.ts)
//   - `#addLibrary=…` -> `parseLibraryTokensFromUrl` (upstream data/library)
// Upstream exposes no helper for the remaining forms, so the two regexes
// below mirror the ones in `initializeScene()` in excalidraw-app/App.tsx
// verbatim. If those ever change, change these with them.

import { parseLibraryTokensFromUrl } from "@excalidraw/excalidraw/data/library";

import { isCollaborationLink } from "../data";

import { docNameFromFileName } from "./importDoc";

/** Mirrors `jsonBackendMatch` in App.tsx `initializeScene()`. */
const RE_JSON_LINK = /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/;
/** Mirrors `externalUrlMatch` in App.tsx `initializeScene()`. */
const RE_EXTERNAL_URL_LINK = /^#url=(.*)$/;

/**
 * `true` when the current URL asks the app to open a specific scene or to
 * import a library — i.e. when the Studio home must be skipped.
 */
export const hasEditorDeepLink = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    if (isCollaborationLink(window.location.href)) {
      return true;
    }
  } catch {
    // `isCollaborationLink` builds a `new URL(...)`; never let a malformed
    // href keep the user out of the Studio.
  }

  if (parseLibraryTokensFromUrl()) {
    return true;
  }

  const hash = window.location.hash;
  if (RE_JSON_LINK.test(hash) || RE_EXTERNAL_URL_LINK.test(hash)) {
    return true;
  }

  return !!new URLSearchParams(window.location.search).get("id");
};

/** A link that carries a whole scene from outside the local store. */
export type ExternalSceneLink = { kind: "json" } | { kind: "url"; url: string };

/**
 * The `#json=` / `#url=` part of the current URL, or `null` for anything else
 * (`#room=` collaboration included, which owns its own scene lifecycle).
 *
 * Read this BEFORE `initializeScene()` in App.tsx runs: that function strips
 * the hash with `history.replaceState` while the scene is still loading, so
 * the link is gone by the time the scene resolves.
 */
export const readExternalSceneLink = (): ExternalSceneLink | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const hash = window.location.hash;
  if (RE_JSON_LINK.test(hash)) {
    return { kind: "json" };
  }
  const externalUrl = hash.match(RE_EXTERNAL_URL_LINK);
  return externalUrl ? { kind: "url", url: externalUrl[1] } : null;
};

/**
 * Doc name for a `#url=` import: the URL's last path segment without its
 * extension, e.g. ".../scenes/Sprint%20plan.excalidraw" -> "Sprint plan".
 *
 * A `#json=` payload is `serializeAsJSON(..., "database")`, which carries no
 * document name, so that path has nothing to derive from and uses `fallback`.
 */
export const docNameFromExternalUrl = (
  url: string,
  fallback: string,
): string => {
  try {
    const decoded = window.decodeURIComponent(url);
    const { pathname } = new URL(decoded, window.location.href);
    return docNameFromFileName(window.decodeURIComponent(pathname), fallback);
  } catch {
    // a malformed or relative-looking url must never block the import
    return fallback;
  }
};
